// Hearth — root app. Simple state-based routing across the three surfaces,
// plus the SRD meta provider (loaded once) and a top-level health gate.
import { Component, createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "./lib/api";
import type { SrdMeta } from "./lib/types";
import { MetaContext } from "./hooks/useMeta";
import { ToastProvider } from "./hooks/useToast";
import { AuthProvider, useAuth } from "./hooks/useAuth";
import { AuthScreen } from "./components/AuthScreen";
import { Lobby } from "./components/Lobby";
import { TableSetup } from "./components/TableSetup";
import { Play } from "./components/Play";
import { CenterLoad, TerminalState } from "./components/ui";

type Route =
  | { name: "lobby" }
  | { name: "setup"; gameId: string }
  | { name: "play"; gameId: string };

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <MetaGate>
          <AuthGate>
            <RouteBoundary>
              <Router />
            </RouteBoundary>
          </AuthGate>
        </MetaGate>
      </AuthProvider>
    </ToastProvider>
  );
}

// Catches render/runtime failures in a live table (a deleted game, a malformed
// snapshot, a socket that hands us garbage) and offers a way back to the lobby
// instead of leaving the player staring at a blank screen. Re-keys on the hash
// so navigating away clears the error automatically.
class RouteBoundary extends Component<{ children: ReactNode }, { error: boolean }> {
  state = { error: false };
  private onHash = () => { if (this.state.error) this.setState({ error: false }); };
  static getDerivedStateFromError() { return { error: true }; }
  componentDidCatch(err: unknown) { console.error("Hearth route error:", err); }
  componentDidMount() { window.addEventListener("hashchange", this.onHash); }
  componentWillUnmount() { window.removeEventListener("hashchange", this.onHash); }
  render() {
    if (this.state.error) {
      return (
        <TerminalState
          icon="🜂"
          title="This table is gone"
          body="The link led nowhere — the game may have ended or been removed. Head back to the lobby to start or join another."
          action={
            <button
              className="btn btn--primary"
              onClick={() => { window.location.hash = "#/"; this.setState({ error: false }); }}
            >
              ← Back to lobby
            </button>
          }
        />
      );
    }
    return this.props.children;
  }
}

const SignInContext = createContext<() => void>(() => {});

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [guest, setGuest] = useState(false);

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CenterLoad label="Checking your session…" />
      </div>
    );
  }
  if (!user && !guest) {
    return <AuthScreen onGuest={() => setGuest(true)} />;
  }
  // Signed in, or browsing as guest. Guests can return to the auth screen.
  return <SignInContext.Provider value={() => setGuest(false)}>{children}</SignInContext.Provider>;
}

function MetaGate({ children }: { children: React.ReactNode }) {
  const [meta, setMeta] = useState<SrdMeta | null>(null);
  const [llm, setLlm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([api.meta(), api.health()])
      .then(([m, h]) => { setMeta(m); setLlm(h.llm); })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to reach the Hearth backend"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <CenterLoad label="Stoking the hearth…" />
      </div>
    );
  }
  if (error || !meta) {
    return (
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, padding: 40, textAlign: "center" }}>
        <div style={{ fontSize: 44 }}>🜂</div>
        <h2 style={{ fontFamily: "var(--serif)", fontSize: 26 }}>The hearth is cold</h2>
        <p className="muted" style={{ maxWidth: 440 }}>{error || "Couldn't load game data."}</p>
        <button className="btn btn--primary" onClick={load}>Try again</button>
      </div>
    );
  }

  return (
    <MetaContext.Provider value={{ meta, loading: false, error: null, llm, reload: load }}>
      {children}
    </MetaContext.Provider>
  );
}

function Router() {
  const [route, setRoute] = useState<Route>(() => readHash());
  const backToSignIn = useContext(SignInContext);

  useEffect(() => {
    const onHash = () => setRoute(readHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const go = useCallback((r: Route) => {
    const hash = r.name === "lobby" ? "#/" :
      r.name === "setup" ? `#/setup/${r.gameId}` : `#/play/${r.gameId}`;
    if (window.location.hash !== hash) window.location.hash = hash;
    setRoute(r);
  }, []);

  switch (route.name) {
    case "setup":
      return (
        <TableSetup gameId={route.gameId}
          onPlay={() => go({ name: "play", gameId: route.gameId })}
          onBack={() => go({ name: "lobby" })} />
      );
    case "play":
      return <Play gameId={route.gameId} onExit={() => go({ name: "setup", gameId: route.gameId })} />;
    default:
      return <Lobby onOpen={(gameId) => go({ name: "setup", gameId })} onSignIn={backToSignIn} />;
  }
}

function readHash(): Route {
  const h = window.location.hash.replace(/^#\/?/, "");
  const parts = h.split("/").filter(Boolean);
  if (parts[0] === "setup" && parts[1]) return { name: "setup", gameId: parts[1] };
  if (parts[0] === "play" && parts[1]) return { name: "play", gameId: parts[1] };
  return { name: "lobby" };
}
